import { useMemo } from 'react';
import { Space, Tag, Typography } from 'antd';
import { TableRowActionsMenu } from '~/app/components/common/TableRowActionsMenu/TableRowActionsMenu';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { KmsHitlQueueItem } from '~/app/services/kms/kms-document.types';
import {
  formatConfidencePercent,
  isAssetLinkHitlReview,
  isOlderThanHours,
  normalizeAssetLinkCandidates,
  normalizeHitlCandidates,
  resolveKmsLinkerCandidates,
} from '~/app/services/kms/kms-document.utils';

type UseKmsHitlQueueColumnsParams = {
  onReview: (item: KmsHitlQueueItem) => void;
};

export function useKmsHitlQueueColumns({
  onReview,
}: UseKmsHitlQueueColumnsParams): ColumnsType<KmsHitlQueueItem> {
  const { t } = useTranslation('kms');

  return useMemo(
    () => [
      {
        title: t('hitl.columns.document'),
        key: 'document',
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{row.fileName}</Typography.Text>
            {row.classifiedDocumentType ? (
              <Tag>{row.classifiedDocumentType}</Tag>
            ) : null}
          </Space>
        ),
      },
      {
        title: t('hitl.columns.uploadedAt'),
        dataIndex: 'createdAt',
        key: 'createdAt',
        render: (value: string) => {
          const label = value ? new Date(value).toLocaleString() : '—';
          const stale = isOlderThanHours(value, 24);
          return (
            <Space>
              <span>{label}</span>
              {stale ? <Tag color="orange">{t('hitl.stale')}</Tag> : null}
            </Space>
          );
        },
      },
      {
        title: t('hitl.columns.aiSuggestion'),
        key: 'suggestion',
        render: (_, row) => {
          const assetReview = isAssetLinkHitlReview({
            linkCandidates: row.linkCandidates,
            linkScope: row.linkScope,
          });
          if (assetReview) {
            const top = normalizeAssetLinkCandidates(
              resolveKmsLinkerCandidates(row),
            )[0];
            if (!top) {
              return t('hitl.noAssetSuggestion');
            }
            return `${top.label} (${formatConfidencePercent(top.score)})`;
          }
          const top = normalizeHitlCandidates(row.classificationCandidates)[0];
          if (!top) {
            return t('hitl.noSuggestion');
          }
          return `${top.documentType ?? top.label} (${formatConfidencePercent(top.score)})`;
        },
      },
      {
        title: t('hitl.columns.confidence'),
        dataIndex: 'classificationConfidence',
        key: 'confidence',
        render: (_, row) => {
          const assetReview = isAssetLinkHitlReview({
            linkCandidates: row.linkCandidates,
            linkScope: row.linkScope,
          });
          if (assetReview) {
            const top = normalizeAssetLinkCandidates(
              resolveKmsLinkerCandidates(row),
            )[0];
            return formatConfidencePercent(top?.score);
          }
          return formatConfidencePercent(row.classificationConfidence);
        },
      },
      {
        title: t('hitl.columns.actions'),
        key: 'actions',
        render: (_, row) => (
          <TableRowActionsMenu
            items={[
              {
                key: 'review',
                label: t('hitl.review'),
                onClick: () => onReview(row),
              },
            ]}
          />
        ),
      },
    ],
    [onReview, t],
  );
}
